import { useMutation } from '@apollo/client';
import type {
  InsertWizardMutation,
  InsertWizardMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertWizardDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertWizardTRPC } from './useInsertWizardTRPC';

export const useInsertWizard = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [insertWizardGraphQL, graphqlState] = useMutation(
    InsertWizardDocument,
    {
      update: (cache) => {
        evictField(cache, 'wizard');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertWizardTRPC();

  const insertWizard = async (
    variables: InsertWizardMutationVariables
  ): Promise<InsertWizardMutation> => {
    if (trpcEnabled) {
      const obj = variables.object;

      return trpcMutation.insertWizard({
        RiskId: obj?.RiskId ?? '',
        AssessmentId: obj?.AssessmentId,
        ActivityId: obj?.ActivityId,
        Status: obj?.Status,
      });
    }

    const result = await insertWizardGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert wizard');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertWizard,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertWizard,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
